import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { withStyles, makeStyles } from "@material-ui/core/styles";
import { TableHead, TableCell, TableRow } from "@material-ui/core";
import { formatHeaderName } from "../../../utils/common/index";
import RetailerPerformaceTableView from "../../../views/retailerPerformance/retailerPerfDashboard";

const useStyles = makeStyles({
  table: {
    minWidth: 500,
  },
  rows: {
    padding: 6,
    textAlign: "right",
  },
  container: {
    maxHeight: 500,
  },
});
const StyledTableCell = withStyles((theme) => ({
  head: {
    //backgroundColor: "#dff0d8",
    borderColor: "#7d8675",
    color: "#3c763d",
    textAlign: "center",
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

export default function RetailerPerformanceTable(props) {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  let otherRetailerLink = false;
  const [showTable, setShowTable] = useState(false);
  const { retailerPerformanceObj } = useSelector(
    (state) => state.retailerPerformanceReducer
  );
  let { retailerPerformanceData } = retailerPerformanceObj;
  let {
    url,
    filterValue,
    headerSelectedDateFrom,
    headerSelectedDateTo,
    triggerRetailerPerformanceAPI,
    searchFields,
  } = props;
  const dispatch = useDispatch();
  let headerKeys,
    sortedRetailerData,
    top20RetailerData,
    individualColumns,
    columnTotal,
    top20ColumnTotal;
  let retailerData = Object.values(retailerPerformanceData)[0];

  const headers = {
    id: searchFields?.partner?.partnerId
      ? searchFields?.partner?.partnerId
      : searchFields?.partner
      ? searchFields?.partner?.split("-")?.pop()?.trim()
      : filterValue.orgId,
    partnerRoleType: searchFields?.partner?.partnerName
      ? searchFields?.partner?.partnerName?.split("-")?.[0]?.trim()
      : searchFields?.partner
      ? searchFields?.partner?.split("-")?.[0]?.trim()
      : filterValue.selectedPartnerRoleName,
    fromDate: headerSelectedDateFrom,
    toDate: headerSelectedDateTo,
    section: props.section,
    moduleName: props.moduleName,
    isRedisActive: "YES",
  };

  useEffect(() => {
    function fetchRetailerPerformanceData() {
      const obj = {
        headers: {
          "Content-Type": "application/json",
          url: url,
        },
        params: headers,
      };
      return dispatch(triggerRetailerPerformanceAPI(obj));
    }
    fetchRetailerPerformanceData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    triggerRetailerPerformanceAPI,
    headerSelectedDateFrom,
    headerSelectedDateTo,
  ]);

  const extractColumns = (item) => {
    const { retailerName, lmtdJiophoneAct, phoneNo, runDate, rrank, ...other } =
      item;
    return { ...other };
  };

  if (retailerData && retailerData.length) {
    const tableHeaders = Object.keys(retailerData[0]);
    const { runDate, rrank, lmtdJiophoneAct, ...headerFields } = tableHeaders;
    headerKeys = Object.values(headerFields).map((item) =>
      formatHeaderName(item)
    );
    sortedRetailerData = retailerData.sort((a, b) =>
      +a.lfmSimAct > +b.lfmSimAct ? -1 : 1
    );
    top20RetailerData = sortedRetailerData.slice(0, 20);
    const top20IndividualColumn = top20RetailerData.map((value) =>
      extractColumns(value)
    );
    top20ColumnTotal = {};
    top20IndividualColumn.map((item) => {
      return Object.entries(item).map(([key, value]) => {
        return top20ColumnTotal[key]
          ? (top20ColumnTotal[key] += +value)
          : (top20ColumnTotal[key] = +value);
      });
    });

    individualColumns = retailerData.map((value) => extractColumns(value));
    columnTotal = {};
    individualColumns.map((item, key) => {
      return Object.entries(item).map(([key, value]) => {
        return columnTotal[key]
          ? (columnTotal[key] += +value)
          : (columnTotal[key] = +value);
      });
    });

    retailerData.length > 20
      ? (otherRetailerLink = true)
      : (otherRetailerLink = false);
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const preventDefault = (event) => event.preventDefault();

  const handlePaginationOption = (event) => {
    preventDefault(event);
    setShowTable(true);
    setPage(1);
  };

  const backToTop20 = (event) => {
    preventDefault(event);
    setShowTable(false);
    setPage(0);
  };

  const daysInLastMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  const retailerNameColorCoding = (rowData) => {
    const runDate = rowData.runDate,
      lfmSimAct = rowData.lfmSimAct,
      mtdSimAct = rowData.mtdSimAct;

    const formattedDate = dayjs(runDate).format("DD-MMM-YYYY");
    let month = dayjs(formattedDate).get("month") + 1,
      day = dayjs(formattedDate).get("date"),
      year = dayjs(formattedDate).get("year"),
      daysMTD = 1,
      colorCode = "";

    if (day > 1) {
      daysMTD = day - 1;
    }

    let lastMonthDays = daysInLastMonth(year, month);

    if (+lfmSimAct >= 0 && +mtdSimAct >= 0) {
      let dailyAvgLFM = (+lfmSimAct / +lastMonthDays).toFixed(2),
        dailyAvgMTD = (+mtdSimAct / +daysMTD).toFixed(2),
        dailyAvgLFM90 = (+dailyAvgLFM * 0.9).toFixed(2);

      if (+dailyAvgMTD < +dailyAvgLFM90) {
        colorCode = "#FF0000";
      } else if (
        +dailyAvgMTD >= +dailyAvgLFM90 &&
        +dailyAvgMTD < +dailyAvgLFM
      ) {
        colorCode = "#EFAC4B";
      } else {
        colorCode = "#000";
      }
    }
    return colorCode;
  };

  const renderTableHeader = (headerKeys) => (
    <TableHead>
      <TableRow>
        <StyledTableCell>Retailer Name</StyledTableCell>
        <StyledTableCell></StyledTableCell>
        <StyledTableCell>FTD Orders</StyledTableCell>
        <StyledTableCell>D-1 Sim Activation</StyledTableCell>
        <StyledTableCell>MTD Sim Activation</StyledTableCell>
        <StyledTableCell>LMTD Sim Activation</StyledTableCell>
        <StyledTableCell>LFM Sim Activation</StyledTableCell>
        <StyledTableCell>MTD Jio Phone Activation</StyledTableCell>
        <StyledTableCell>Wallet Balance</StyledTableCell>
        <StyledTableCell>Jio Phone Stock</StyledTableCell>
      </TableRow>
    </TableHead>
  );
  return (
    <RetailerPerformaceTableView
      filterValue={props.filterValue}
      timestampArr={props.timestampArr}
      retailerPerformanceObj={retailerPerformanceObj}
      retailerData={retailerData}
      classes={classes}
      showTable={showTable}
      page={page}
      backToTop20={backToTop20}
      section={props.section}
      renderTableHeader={renderTableHeader}
      headerKeys={headerKeys}
      rowsPerPage={rowsPerPage}
      sortedRetailerData={sortedRetailerData}
      StyledTableCell={StyledTableCell}
      retailerNameColorCoding={retailerNameColorCoding}
      otherRetailerLink={otherRetailerLink}
      handlePaginationOption={handlePaginationOption}
      columnTotal={columnTotal}
      top20ColumnTotal={top20ColumnTotal}
      handleChangePage={handleChangePage}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
    />
  );
}

RetailerPerformanceTable.propTypes = {
  url: PropTypes.string,
  filterValue: PropTypes.object,
  headerSelectedDateFrom: PropTypes.string,
  headerSelectedDateTo: PropTypes.string,
  triggerRetailerPerformanceAPI: PropTypes.func,
};
